import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createBPOnboardingTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. I'm calling to help
          you get started with your Remote Patient Monitoring program. For
          quality and compliance purposes, this call may be recorded. To protect
          your privacy, can you please confirm your full name and date of
          birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'onboardingPurposeReassurance',
      type: 'checkbox',
      title: <p>Onboarding Purpose & Reassurance</p>,
      description: (
        <ScriptWrapper>
          "The goal of today's call is to get your blood pressure device set up,
          make sure everything is working, and help you take your first reading
          if we're able."
          <br />
          "There's no way to do this wrong — I'll guide you step by step, and if
          something doesn't work right away, that's completely okay."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Purpose explained',
          value: 'purposeExplained',
        },
        {
          key: makeid(10),
          label: 'Reassurance provided',
          value: 'reassuranceProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'earlyGoalSetting',
      type: 'checkbox',
      title: <p>Early Goal Setting (Clinical Best Practice)</p>,
      description: (
        <ScriptWrapper>
          "As part of your care, your clinic recommends checking your blood
          pressure and heart rate as often as possible, ideally daily."
          <br />
          "This helps establish your normal range and allows your care team to
          notice changes early."
          <br />
          "If you ever miss a day, that's okay — consistency over time is what
          matters most."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Daily readings encouraged as best practice',
          value: 'dailyReadingsEncouragedAsBestPractice',
        },
        {
          key: makeid(10),
          label: 'Flexibility and consistency emphasized',
          value: 'flexibilityAndConsistencyEmphasized',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceReadiness',
      type: 'radio',
      title: <p>Device Readiness Check</p>,
      description: (
        <ScriptWrapper>
          "Do you have your blood pressure device with you right now?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Device present during call',
          value: 'devicePresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — let's get everything ready together."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Device not present during call',
          value: 'deviceNotPresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "That's okay — we can review how to use it today, and you can
                  take your first reading later."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cuffPlacementReviewed',
      type: 'checkbox',
      title: <p>Cuff Placement Guidance</p>,
      description: (
        <ScriptWrapper>
          "Let's start by placing the cuff."
          <br />
          "The cuff should go directly on bare skin, not over clothing."
          <br />
          "Place it on your upper arm, about one inch above your elbow."
          <br />
          "The tubing should run down the inside of your arm."
          <br />
          "It should feel snug but not tight."
          <br />
          "Let me know when that feels comfortable."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Bare arm placement reviewed',
          value: 'bareArmPlacementReviewed',
        },
        {
          key: makeid(10),
          label: 'Correct position above elbow reviewed',
          value: 'correctPositionAboveElbowReviewed',
        },
        {
          key: makeid(10),
          label: 'Proper snugness confirmed',
          value: 'properSnugnessConfirmed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'bodyPositionRestingReviewed',
      type: 'checkbox',
      title: <p>Body Position & Resting Instructions</p>,
      description: (
        <ScriptWrapper>
          "Now let's make sure you're positioned correctly."
          <br />
          "Sit with your feet flat on the floor."
          <br />
          "Rest your arm on a table or armrest so it's at about heart level."
          <br />
          "Try to sit quietly without talking for about one minute."
          <br />
          "Let me know when you're ready."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Feet flat on floor',
          value: 'feetFlatOnFloor',
        },
        {
          key: makeid(10),
          label: 'Arm supported at heart level',
          value: 'armSupportedAtHeartLevel',
        },
        {
          key: makeid(10),
          label: 'Resting period completed',
          value: 'restingPeriodCompleted',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceModel',
      type: 'radio',
      title: <p>Device Model</p>,
      description: (
        <ScriptWrapper>
          MA GUIDANCE (DO NOT READ ALOUD):
          <br />
          <br />
          <ul>
            <li>• Confirm device model (BP01, U807ION/U807, A600N)</li>
            <li>• Verify device has power (battery or charge)</li>
            <li>• Be prepared to coach the patient based on model</li>
          </ul>
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'BP01',
          value: 'BP01',
        },
        {
          key: makeid(10),
          label: 'U807ION',
          value: 'U807ION',
        },
        {
          key: makeid(10),
          label: 'U807',
          value: 'U807',
        },
        {
          key: makeid(10),
          label: 'A600N',
          value: 'A600N',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceSetupFirstReadingInitiation',
      type: 'checkbox',
      title: <p>Device Setup & First Reading Initiation</p>,
      description: (
        <ScriptWrapper>
          PATIENT-FACING SCRIPT: "Now we're going to start the reading. I'll
          guide you."
          <br />
          If BP01: "Please press the blue button to begin."
          <br />
          If U807ION or U807: "Please press the yellow button to begin."
          <br />
          If A600N: "Please press the green button to begin."
          <br />
          If patient hesitates or nothing happens: "That's okay — sometimes it
          takes a second. Let me know what you see on the screen."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'MA guided patient through model-specific start instructions',
          value: 'maGuidedPatientThroughModelSpecificStartInstructions',
        },
        {
          key: makeid(10),
          label: 'First reading completed successfully',
          value: 'firstReadingCompletedSuccessfully',
        },
        {
          key: makeid(10),
          label: 'Reading attempted but unsuccessful',
          value: 'readingAttemptedButUnsuccessful',
        },
        {
          key: makeid(10),
          label: 'Reading deferred',
          value: 'readingDeferred',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'readingTransmission',
      type: 'radio',
      title: <p>Reading Completion & Confirmation</p>,
      description: (
        <ScriptWrapper>
          "When the cuff deflates, you should see your blood pressure and heart
          rate on the screen."
          <br />
          "Please tell me the numbers you see."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Reading transmitted successfully',
          value: 'readingTransmittedSuccessfully',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Perfect — you did it. That reading went through."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Transmission pending or failed',
          value: 'transmissionPendingOrFailed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "That's okay — sometimes the first try doesn't work. We can
                  try again or follow up later."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'programValueReinforced',
      type: 'checkbox',
      title: <p>Program Value & Engagement Reinforcement</p>,
      description: (
        <ScriptWrapper>
          "Your readings are securely shared with your care team, who review
          trends over time."
          <br />
          "Even normal readings are important — they show what's working and
          help catch changes early."
          <br />
          "If readings are missing for an extended period, you may receive
          reminder texts or a call from us — just to make sure everything's okay
          and see if you need help."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Trend monitoring explained',
          value: 'trendMonitoringExplained',
        },
        {
          key: makeid(10),
          label: 'Engagement encouraged',
          value: 'engagementEncouraged',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we have on file for you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone number confirmed',
          value: 'cellPhoneNumberConfirmed',
        },
        {
          key: makeid(10),
          label: 'Cell phone number updated',
          value: 'cellPhoneNumberUpdated',
        },
        {
          key: makeid(10),
          label: 'Patient reports no cell phone',
          value: 'patientReportsNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Scheduling</p>,
      description: (
        <ScriptWrapper>
          "As part of the program, a monthly phone call with our team is
          required."
          <br />
          "These calls help us check in, answer questions, and make sure
          everything continues to work smoothly."
          <br />
          "When your readings stay within your doctor's recommended range,
          they're reviewed as trends over time."
          <br />
          "If a reading falls outside of that range, your clinic may reach out —
          often with a call from a nurse — to check in and decide if follow-up
          is needed."
          <br />
          "Would you like to schedule your first monthly check-in now, or should
          we reach out closer to when it's due?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monthly phone call requirement reviewed',
          value: 'monthlyPhoneCallRequirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Program monitoring process explained',
          value: 'programMonitoringProcessExplained',
        },
        {
          key: makeid(10),
          label: 'Nurse or clinic outreach for out-of-range readings explained',
          value: 'nurseOrClinicOutreachForOutOfRangeReadingsExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TBPOnboardingTemplateValues = ExtractStepValues<
  ReturnType<typeof createBPOnboardingTemplate>
>;

export const createBPOnboardingBlurb = (
  data: TBPOnboardingTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    onboardingPurposeReassurance,
    earlyGoalSetting,
    deviceReadiness,
    cuffPlacementReviewed,
    bodyPositionRestingReviewed,
    deviceModel,
    deviceSetupFirstReadingInitiation,
    readingTransmission,
    programValueReinforced,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');

    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    onboardingPurposeReassurance?.length &&
    !hasField(ctx, 'onboardingPurposeReassurance')
  ) {
    addField(ctx, 'onboardingPurposeReassurance');

    const map = {
      purposeExplained: 'Onboarding call purpose explained.',
      reassuranceProvided:
        'Patient reassured regarding device setup and informed that support is available if issues arise.',
    };
    txt.push(...mapValuesToText(onboardingPurposeReassurance, map));
  }

  if (earlyGoalSetting?.length && !hasField(ctx, 'earlyGoalSetting')) {
    addField(ctx, 'earlyGoalSetting');

    const map = {
      dailyReadingsEncouragedAsBestPractice:
        'Clinical best practice for frequent blood pressure and heart rate readings discussed.',
      flexibilityAndConsistencyEmphasized:
        'Importance of consistency over time emphasized, with reassurance that occasional missed readings are acceptable.',
    };
    txt.push(...mapValuesToText(earlyGoalSetting, map));
  }

  if (deviceReadiness && !hasField(ctx, 'deviceReadiness')) {
    addField(ctx, 'deviceReadiness');

    const map = {
      devicePresentDuringCall: 'present during call',
      deviceNotPresentDuringCall: 'not present during call',
    };
    txt.push(`Device availability reviewed. Device ${map[deviceReadiness]}.`);
    if (deviceReadiness === 'deviceNotPresentDuringCall') {
      txt.push(
        'Patient provided with education and instructed to complete first reading at a latertime.',
      );
    }
  }

  if (
    cuffPlacementReviewed?.length &&
    !hasField(ctx, 'cuffPlacementReviewed')
  ) {
    addField(ctx, 'cuffPlacementReviewed');

    const map = {
      bareArmPlacementReviewed: 'bare arm placement',
      correctPositionAboveElbowReviewed: 'correct position above elbow',
      properSnugnessConfirmed: 'appropriate snugness',
    };
    const items = mapValuesToText(cuffPlacementReviewed, map);
    if (items.length) {
      txt.push(
        `Cuff placement reviewed with patient, including ${items.join(', ')}.`,
      );
    }
  }

  if (
    bodyPositionRestingReviewed?.length &&
    !hasField(ctx, 'bodyPositionRestingReviewed')
  ) {
    addField(ctx, 'bodyPositionRestingReviewed');

    const map = {
      feetFlatOnFloor: 'feet flat on the floor',
      armSupportedAtHeartLevel: 'arm supported at heart level',
      restingPeriodCompleted: 'resting period completed',
    };
    const items = mapValuesToText(bodyPositionRestingReviewed, map);
    if (items.length) {
      txt.push(
        `Proper body positioning and resting instructions reviewed and confirmed prior to initiating first reading, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (deviceModel && !hasField(ctx, 'deviceModel')) {
    addField(ctx, 'deviceModel');

    txt.push(
      `MA guided patient through model-specific device start instructions for ${deviceModel}.`,
    );
  }

  if (
    deviceSetupFirstReadingInitiation?.length &&
    !hasField(ctx, 'deviceSetupFirstReadingInitiation')
  ) {
    addField(ctx, 'deviceSetupFirstReadingInitiation');

    const map = {
      maGuidedPatientThroughModelSpecificStartInstructions:
        'MA guided patient through model-specific start instructions',
      firstReadingCompletedSuccessfully: 'First reading completed successfully',
      readingAttemptedButUnsuccessful: 'Reading attempted but unsuccessful',
      readingDeferred: 'Reading deferred',
    };
    const items = mapValuesToText(deviceSetupFirstReadingInitiation, map);

    txt.push(
      `First blood pressure and heart rate reading ${items.join(', ')}.`,
    );
  }

  if (readingTransmission && !hasField(ctx, 'readingTransmission')) {
    addField(ctx, 'readingTransmission');

    if (readingTransmission === 'readingTransmittedSuccessfully') {
      txt.push(`Reading transmission successful.`);
    }

    if (readingTransmission === 'transmissionPendingOrFailed') {
      txt.push(
        `Reading transmission pending or unsuccessful. Issue documented and follow-up planned as appropriate.`,
      );
    }
  }

  if (
    programValueReinforced?.length &&
    !hasField(ctx, 'programValueReinforced')
  ) {
    addField(ctx, 'programValueReinforced');

    const map = {
      trendMonitoringExplained:
        'how normal and out-of-range readings support trend monitoring',
      engagementEncouraged:
        'the importance of ongoing engagement and early intervention by the care team',
    };

    const items = mapValuesToText(programValueReinforced, map);

    if (items.length) {
      txt.push(`Program value reviewed, including ${formatList(items)}.`);
    }
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');

    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file and updated in patient chart as needed.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');

    const map = {
      cellPhoneNumberConfirmed: 'confirmed',
      cellPhoneNumberUpdated: 'updated',
      patientReportsNoCellPhone: 'patient reports no cell phone',
    };
    txt.push(
      `Cell phone contact verified with patient ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');

    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');

    const map = {
      monthlyPhoneCallRequirementReviewed: 'monthly phone call requirement',
      programMonitoringProcessExplained: 'program monitoring process explained',
      nurseOrClinicOutreachForOutOfRangeReadingsExplained:
        'that out-of-range readings may result in nurse or clinic outreach',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');

    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join(' ');
};
